import React from 'react';
import cx from 'classnames';
import { FormattedMessage } from 'react-intl';
import { OrderedMap } from 'immutable';
import PropTypes from 'prop-types';

import { ParkingPass as ParkingPassModel } from 'models/parking-passes';
import Ticket, { MAX_EDIT_LICENSE_PLATE_IN_MINS } from 'models/ticket';
import UserModel from 'models/user';

import * as AppContext from 'lib/app-context';
import { parkingPassLicenseEditDisabled } from 'lib/common/messages';
import LicensePlateModal from 'components/vehicles/modal/license-plate';

class LicensePlateSection extends React.Component {
  constructor(props) {
    super(props);

    this.renderLicensePlateModal = this.renderLicensePlateModal.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.showLicensePlateModal = this.showLicensePlateModal.bind(this);
    this.hideLicensePlateModal = this.hideLicensePlateModal.bind(this);
    this.passHasPlate = this.passHasPlate.bind(this);
    this.renderPlateText = this.renderPlateText.bind(this);
    this.plateClasses = this.plateClasses.bind(this);
    this.renderButtonText = this.renderButtonText.bind(this);

    this.state = {
      scanCodeDismissed: false,
      showLicensePlateModal: false,
      guestPlate: null,
    };
  }

  canUpdateLicensePlate() {
    const { parkingPass, ticket } = this.props;

    if (!ticket) return !parkingPass.hasReachedEndTime;

    return !parkingPass.hasReachedEndTime && ticket.canUpdateLicensePlate;
  }

  get withoutPlateButCanEdit() {
    return !this.passHasPlate() && this.canUpdateLicensePlate();
  }

  get withoutPlateAndCannotEdit() {
    return !this.passHasPlate() && !this.canUpdateLicensePlate();
  }

  get withPlateAndCanEdit() {
    return this.passHasPlate() && this.canUpdateLicensePlate();
  }

  get withPlateButCannotEdit() {
    return this.passHasPlate() && !this.canUpdateLicensePlate();
  }

  submitForm(input) {
    const { parkingPass, addVehicle, addMessage } = this.props;
    if (!this.canUpdateLicensePlate()) {
      addMessage(parkingPassLicenseEditDisabled);
      this.hideLicensePlateModal();
      return false;
    }
    addVehicle({
      ...input,
      bookingId: parkingPass.id,
    });
    this.hideLicensePlateModal();
    this.setState({ guestPlate: input.plateNumber });
  }

  plateClasses() {
    const hasPlate = this.passHasPlate();

    return cx(
      'h5-sg padding-0 col-xs-12 text-weight-bold text-color-dark-slate-lte-sm col-print-12',
      {
        'text-color-dark-slate-lte-sm': hasPlate,
        'text-color-white': !hasPlate,
        'vertical-padding-top': !hasPlate,
      }
    );
  }

  passHasPlate() {
    const { vehicles, parkingPass } = this.props;
    return !!(parkingPass.getVehicle(vehicles) || this.state.guestPlate);
  }

  showLicensePlateModal() {
    const { addMessage } = this.props;
    if(this.canUpdateLicensePlate()) {
      this.setState({ showLicensePlateModal: true });
    } else {
      addMessage(parkingPassLicenseEditDisabled)
    }
  }

  hideLicensePlateModal() {
    this.setState({ showLicensePlateModal: false });
  }

  renderPlateText() {
    const { vehicles, parkingPass } = this.props;
    const { guestPlate } = this.state;
    const vehicle = parkingPass.getVehicle(vehicles);

    if (vehicle) {
      return vehicle.get('plateNumber');
    }
    if (guestPlate) { return guestPlate; }

    if (!this.canUpdateLicensePlate() && !this.passHasPlate()) {
      return (<FormattedMessage
        id="parking-pass.no-vehicle-added"
        defaultMessage="No Vehicle Added"
      />);
    }

    return (<FormattedMessage
      id="parking-pass.no-vehicle"
      defaultMessage="No Vehicle"
    />);
  }

  renderLicensePlateModal() {
    if (!this.state.showLicensePlateModal) {
      return null;
    }
    return (
      <LicensePlateModal
        user={this.props.user}
        vehicles={this.props.vehicles}
        submitForm={this.submitForm}
        onClose={this.hideLicensePlateModal}
      />
    );
  }

  renderLicenseInstructions() {
    const { ticket, parkingPass } = this.props;
    if (!ticket || parkingPass.hasReachedEndTime || !ticket.isSTP) return false;

    let messageProps = {
      values: { maxUpdateTreshold: MAX_EDIT_LICENSE_PLATE_IN_MINS },
    };

    if (this.withPlateButCannotEdit) {
      messageProps = {
        id: "parking-pass.with-plate-cannot-update",
        defaultMessage: "License plate numbers can only be changed within {maxUpdateTreshold} minutes of booking.",
        values: { maxUpdateTreshold: MAX_EDIT_LICENSE_PLATE_IN_MINS },
      };
    } else if (this.withoutPlateAndCannotEdit) {
      messageProps = {
        id: "parking-pass.without-plate-cannot-update",
        defaultMessage: "License plate numbers can only be changed within {maxUpdateTreshold} minutes of booking. Please see a parking attendant for assistance.",
        values: { maxUpdateTreshold: MAX_EDIT_LICENSE_PLATE_IN_MINS },
      };
    } else if (this.withPlateAndCanEdit) {
      messageProps = {
        id: "parking-pass.with-plate-update-reminder",
        defaultMessage: "Please confirm your license plate number is correct as you can only change it until {maxUpdateTime}",
        values: { maxUpdateTime: ticket.formattedUpdateBefore },
      };
    } else if (this.withoutPlateButCanEdit) {
      messageProps = {
        id: "parking-pass.without-plate-update-reminder",
        defaultMessage: "This parking pass is not valid without adding a license plate. You have until {maxUpdateTime} to enter.",
        values: { maxUpdateTime: ticket.formattedUpdateBefore },
      };
    }

    return messageProps && <FormattedMessage {...messageProps} />;
  }

  renderButtonText() {
    if (!this.canUpdateLicensePlate()) return;

    if (this.passHasPlate()) {
      const { appContext } = this.props;
      const isMobile = appContext === AppContext.MOBILE;
      return <>
        <span className="iconified iconified-pencil-border margin-right-10" />
        <FormattedMessage
          id={`parking-pass.edit${isMobile ? "" : "-vehicle"}`}
          defaultMessage={`EDIT ${isMobile ? "" : "VEHICLE"}`}
        />
      </>;
    } else {
      return <>
        <span className="iconified iconified-plus-circle margin-right-10" />
        <FormattedMessage id="parking-pass.add-vehicle" defaultMessage="ADD VEHICLE" />
      </>;
    }
  }

  render() {
    const licenseInstructionsText = this.renderLicenseInstructions();

    const buttonClasses = cx("btn btn-lg edit-vehicle-btn padding-2x hidden-print", {
      "margin-left-15": this.canUpdateLicensePlate(),
      "missing-plate": this.canUpdateLicensePlate() && !this.passHasPlate(),
    });

    return (
      <div>
        <div className="horizontal-padding row text-center validation-box">
          {licenseInstructionsText && <p className="text-color-dark-slate-lte-sm text-center license-instructions">{licenseInstructionsText}</p>}
          <p className={this.plateClasses()}>
            <FormattedMessage
              id="parking-pass.license-plate"
              defaultMessage="License Plate:"
            />
            {" "}
            {this.renderPlateText()}
            {this.canUpdateLicensePlate() && <span className={buttonClasses}  onClick={this.showLicensePlateModal}>
                {this.renderButtonText()}
            </span>}
          </p>
        </div>
        {this.renderLicensePlateModal()}
      </div>
    );
  }
}

LicensePlateSection.propTypes = {
  appContext: PropTypes.string.isRequired,
  parkingPass: PropTypes.instanceOf(ParkingPassModel).isRequired,
  vehicles: PropTypes.instanceOf(OrderedMap).isRequired,
  user: PropTypes.instanceOf(UserModel).isRequired,
  addMessage: PropTypes.func.isRequired,
  addVehicle: PropTypes.func.isRequired,
  ticket: PropTypes.instanceOf(Ticket),
};

export default LicensePlateSection;
